import React, { useState, useEffect, useRef, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { getHandhavingGemeenten, getHandhavingAnalyse, getHandhavingBestanden, bewaarHandhavingInstellingen,
         leesHandhavingBestandIn, downloadHandhaving, getHandhavingGrenshulp, getHandhavingTrend } from './api';

// De Monitoring Module Handhaving: een eigen scherm, alleen voor de beheerder.
// Alle cijfers komen van de server (handhaving.js); dit scherm toont ze alleen.

// Twee lagen van CARTO: een lichte ondergrond zonder namen, en daarboven de
// straatnamen in een donkerder grijs, zodat ze bij het inzoomen leesbaar zijn.
const CARTO_SLEUTEL = process.env.REACT_APP_CARTO_KEY ? `?key=${process.env.REACT_APP_CARTO_KEY}` : '';
const KAART_ONDERGROND = 'https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png' + CARTO_SLEUTEL;
const KAART_NAMEN = 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}{r}.png' + CARTO_SLEUTEL;

const N = {
  ink:'#12161C', ink2:'#3D4652', ink3:'#6B7684', ink4:'#9AA4B1',
  paper:'#FFFFFF', paper2:'#FBFAF7', canvas:'#ECEAE5', line:'#D9D5CD', line2:'#C4BFB5', head:'#F6F4F0',
  accent:'#1F5C6B', accent2:'#2D7F92', goud:'#CDA85B', paars:'#7B4F9E', warn:'#9C6420', warnBg:'#F7EBD8', red:'#B4442F',
};
// Kleuren op de kaart: goed van elkaar te onderscheiden, geen groen naast petrol.
const SOORT_KLEUR = { terugkerend: N.accent, meerdere: N.paars, eenmalig: N.goud };
const SOORT_TEKST = { terugkerend: 'Terugkerend', meerdere: 'Meerdere keren', eenmalig: 'Eenmalig' };
const FONT = "-apple-system,BlinkMacSystemFont,'Inter','Segoe UI',system-ui,sans-serif";
const MAANDEN = ['januari','februari','maart','april','mei','juni','juli','augustus','september','oktober','november','december'];

function datumTekst(t) {
  if (!t) return '';
  const d = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Brussels', year: 'numeric', month: '2-digit', day: '2-digit' })
    .format(new Date(t)).split('-').map(Number);
  return `${d[2]} ${MAANDEN[d[1] - 1]} ${d[0]}`;
}
function uurTekst(t) {
  if (!t) return '';
  const u = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Brussels', hour: '2-digit', hourCycle: 'h23' }).format(new Date(t));
  return `${Number(u)}.00 uur`;
}
const getal = (n) => (n == null ? '' : Number(n).toLocaleString('nl-BE'));
const pct = (f) => (f == null ? '' : `${Math.round(f * 100)} %`);
const esc = (t) => String(t ?? '').replace(/[&<>"']/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));

const st = {
  pagina: { minHeight: '100vh', background: N.canvas, color: N.ink, fontFamily: FONT, fontSize: 15 },
  top: { background: N.ink, height: 56, display: 'flex', alignItems: 'center', gap: 14, padding: '0 28px' },
  logo: { background: N.accent2, color: '#fff', fontSize: 14, fontWeight: 600, borderRadius: 6, padding: '5px 12px' },
  tknop: { fontFamily: 'inherit', fontSize: 12.5, color: 'rgba(255,255,255,.72)', padding: '6px 12px',
           border: '1px solid rgba(255,255,255,.16)', borderRadius: 7, background: 'transparent', cursor: 'pointer', whiteSpace: 'nowrap' },
  breed: { maxWidth: 1240, margin: '0 auto', padding: '0 28px' },
  eyebrow: { fontSize: 13, letterSpacing: '.12em', textTransform: 'uppercase', color: N.accent, fontWeight: 700 },
  h1: { margin: '6px 0 4px', fontSize: 30, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' },
  select: { fontFamily: 'inherit', fontSize: 15, padding: '6px 10px', border: `1px solid ${N.line2}`, borderRadius: 7, background: '#fff' },
  meetband: { marginTop: 14, display: 'flex', flexWrap: 'wrap', gap: '8px 22px', fontSize: 14, color: N.ink2 },
  melding: { marginTop: 14, background: N.warnBg, border: '1px solid #E8D3AC', color: N.warn, borderRadius: 8, padding: '9px 12px', fontSize: 14 },
  fout: { marginTop: 14, background: '#FBEDE7', border: '1px solid #EFC3B1', color: N.red, borderRadius: 8, padding: '9px 12px', fontSize: 14 },
  tabs: { display: 'flex', gap: 2, marginTop: 20, borderBottom: `1px solid ${N.line2}`, overflowX: 'auto' },
  tab: (aan) => ({ fontFamily: 'inherit', fontSize: 14, padding: '10px 16px', border: 'none', background: 'none', cursor: 'pointer',
                   color: aan ? N.accent : N.ink3, borderBottom: `2px solid ${aan ? N.accent : 'transparent'}`, fontWeight: aan ? 600 : 400, whiteSpace: 'nowrap' }),
  tegels: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 18 },
  tegel: { background: N.paper, border: `1px solid ${N.line2}`, borderRadius: 10, padding: '14px 16px' },
  tegelN: { fontSize: 26, fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
  tegelL: { fontSize: 13, color: N.ink3, marginTop: 2 },
  kaart: { background: N.paper, border: `1px solid ${N.line2}`, borderRadius: 10, overflow: 'hidden', marginBottom: 18 },
  kaartKop: { margin: 0, fontSize: 16, padding: '14px 18px', borderBottom: `1px solid ${N.line}`, display: 'flex',
              justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  klein: { fontWeight: 400, fontSize: 13, color: N.ink3 },
  table: { width: '100%', borderCollapse: 'collapse', fontVariantNumeric: 'tabular-nums' },
  th: { textAlign: 'left', fontSize: 13, fontWeight: 600, color: N.ink3, background: N.head, padding: '9px 12px',
        borderBottom: `1px solid ${N.line}`, whiteSpace: 'nowrap' },
  thG: { textAlign: 'right', width: 120 },
  td: { fontSize: 15, padding: '10px 12px', borderBottom: `1px solid ${N.line}` },
  voet: { fontSize: 12.5, color: N.ink3, padding: '10px 18px 14px', lineHeight: 1.5 },
  knop: { fontFamily: 'inherit', fontSize: 14, padding: '7px 14px', borderRadius: 7, border: `1px solid ${N.accent}`,
          background: N.accent, color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap' },
  knopLicht: { fontFamily: 'inherit', fontSize: 14, padding: '7px 14px', borderRadius: 7, border: `1px solid ${N.accent}`,
               background: '#fff', color: N.accent, cursor: 'pointer', whiteSpace: 'nowrap' },
  velden: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, padding: 18 },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: N.ink2, marginBottom: 6 },
  input: { width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', fontSize: 15, padding: '8px 10px',
           border: `1px solid ${N.line2}`, borderRadius: 7, background: '#fff' },
  hulp: { fontSize: 12.5, color: N.ink3, marginTop: 5 },
  schakel: { display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 14, fontSize: 14, color: N.ink2, cursor: 'pointer' },
  schakelKop: { display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 400, color: N.ink2, cursor: 'pointer' },
  pil: (soort) => ({ display: 'inline-block', fontSize: 12.5, padding: '2px 9px', borderRadius: 999,
                     background: soort === 'eenmalig' ? '#EFEDE8' : soort === 'meerdere' ? 'rgba(123,79,158,.12)' : 'rgba(31,92,107,.12)',
                     color: soort === 'eenmalig' ? N.ink3 : soort === 'meerdere' ? N.paars : N.accent }),
};
const tdG = { ...st.td, textAlign: 'right' };
const thG = { ...st.th, ...st.thG };

function Tabel({ kop, children }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={st.table}>
        <thead><tr>{kop.map((k, i) => <th key={i} style={k.g ? thG : st.th}>{k.t}</th>)}</tr></thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

// Laadpunten op dezelfde plek en van dezelfde exploitant komen in één regel.
// Een groot terrein geeft anders tientallen regels met hetzelfde adres.
const SOORT_GEWICHT = { terugkerend: 3, meerdere: 2, eenmalig: 1 };
function perLocatie(lijst) {
  const groepen = new Map();
  for (const p of lijst || []) {
    const plek = p.adres || (p.lat != null ? `${p.lat.toFixed(4)},${p.lng.toFixed(4)}` : p.laadpunt);
    const sl = `${plek}|${p.exploitant}`;
    if (!groepen.has(sl)) groepen.set(sl, { sleutel: sl, adres: p.adres, exploitant: p.exploitant, wijk: p.wijk, punten: [] });
    groepen.get(sl).punten.push(p);
  }
  return [...groepen.values()].map(g => {
    const tel = new Map();
    for (const p of g.punten) if (p.drukste) tel.set(p.drukste, (tel.get(p.drukste) || 0) + 1);
    const soort = g.punten.reduce((z, p) => ((SOORT_GEWICHT[p.soort] || 0) > (SOORT_GEWICHT[z] || 0) ? p.soort : z), null);
    return { ...g, soort,
      urenBoven: g.punten.reduce((t, p) => t + (p.urenBoven || 0), 0),
      wekenLang: Math.max(...g.punten.map(p => p.wekenLang || 0)),
      wekenBekeken: g.punten[0].wekenBekeken,
      rotatietarief: [...new Set(g.punten.map(p => p.rotatietarief).filter(Boolean))].join(', '),
      drukste: [...tel.entries()].sort((x, y) => y[1] - x[1])[0]?.[0] || '' };
  }).sort((x, y) => (SOORT_GEWICHT[y.soort] || 0) - (SOORT_GEWICHT[x.soort] || 0) || y.urenBoven - x.urenBoven);
}
const aantalTekst = (n) => (n === 1 ? '1 laadpunt' : `${getal(n)} laadpunten`);

const ongeveer = (n) => (n >= 100 ? Math.round(n / 10) * 10 : n);

function Uitleg({ instellingen, gemeente, toegang, onSluit }) {
  const ins = instellingen || {};
  const rot = ins.rotatie || {};
  const [toonBegrippen, setToonBegrippen] = useState(false);
  const van = ins.controle?.van ?? 8, tot = ins.controle?.tot ?? 20;
  const rotVan = rot.van ?? 8, rotTot = rot.tot ?? 20;
  const weken = ins.patroonWeken ?? 4, minimum = ins.patroonMinimum ?? 3, keren = ins.patroonKeren ?? 1;
  const grens = ins.grensLangUren;
  const grensTekst = grens != null ? `${getal(grens)} uur` : 'de grens bij Instellingen';
  useEffect(() => {
    const t = (e) => { if (e.key === 'Escape') onSluit(); };
    document.addEventListener('keydown', t);
    return () => document.removeEventListener('keydown', t);
  }, [onSluit]);
  const tekst = { fontSize: 14.5, lineHeight: 1.55, color: N.ink2, margin: 0 };
  const sectie = { fontSize: 13, letterSpacing: '.08em', textTransform: 'uppercase', color: N.accent, fontWeight: 700, margin: '22px 0 10px' };
  const blok = { border: `1px solid ${N.line}`, borderRadius: 10, padding: '14px 16px', background: N.paper2 };
  const blokKop = { margin: '0 0 6px', fontSize: 15.5, color: N.ink };
  const lijstje = { ...tekst, paddingLeft: 18, display: 'grid', gap: 4 };
  const label = { display: 'inline-block', fontSize: 12, fontWeight: 600, color: N.accent, background: '#EAF2F4', borderRadius: 999, padding: '2px 9px', marginBottom: 8 };
  const raster = (min) => ({ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`, gap: 12 });
  const stappen = [
    ['Meten', 'Elk uur wordt van elk publiek laadpunt vastgelegd of het in gebruik is.'],
    ['Te lang bezet', `Staat een laadpunt langer dan ${grensTekst} aaneen in gebruik, gerekend vanaf het begin, dan telt het als te lang bezet. Dit geldt tussen ${van} en ${tot} uur.`],
    ['Terugkerend', `Gebeurt dat bij hetzelfde laadpunt minstens ${keren} keer per week, in minstens ${minimum} van de laatste ${weken} weken, dan is het terugkerend. Daar loont een controle.`],
    ['Controleren', 'Controle gebeurt ter plaatse, of de gemeente stuurt de lijst naar de exploitant. Die kan de bestuurder vragen de auto eerder te verplaatsen.'],
  ];
  return (
    <div onClick={onSluit} style={{ position: 'fixed', inset: 0, background: 'rgba(18,22,28,.55)', display: 'flex',
      alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto', zIndex: 2000 }}>
      <div role="dialog" aria-modal="true" aria-labelledby="uitleg-titel" onClick={e => e.stopPropagation()}
           style={{ background: '#fff', borderRadius: 12, maxWidth: 900, width: '100%', padding: '24px 26px 26px', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <h2 id="uitleg-titel" style={{ margin: 0, fontSize: 22 }}>Uitleg en werkwijze</h2>
          <button type="button" style={st.knopLicht} onClick={onSluit}>Sluiten</button>
        </div>
        <p style={{ ...tekst, fontSize: 15.5, marginTop: 10 }}>Deze module laat zien waar en wanneer laadpunten te lang bezet zijn, zodat controles gericht kunnen gebeuren. Het gaat om plekken en tijden, niet om individuele auto's.</p>

        <h3 style={sectie}>Hoe het werkt</h3>
        <ol style={{ ...raster(190), listStyle: 'none', padding: 0, margin: 0 }}>
          {stappen.map(([kop, t], i) => (
            <li key={kop} style={{ ...blok, background: '#fff', borderTop: `3px solid ${N.accent}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ width: 24, height: 24, borderRadius: 999, background: N.accent, color: '#fff', fontSize: 13, fontWeight: 700,
                               display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>{i + 1}</span>
                <b style={{ fontSize: 15, color: N.ink }}>{kop}</b>
              </div>
              <p style={tekst}>{t}</p>
            </li>))}
        </ol>

        <h3 style={sectie}>Instellen en volgen</h3>
        <div style={raster(300)}>
          <div style={blok}>
            <span style={label}>Tabblad Instellingen</span>
            <h4 style={blokKop}>Grens en instellingen kiezen</h4>
            <ul style={lijstje}>
              <li>Controle-uren, grens voor te lang bezet en de regels voor terugkerend staan op tabblad Instellingen.</li>
              <li>De knop Hulp bij kiezen grens toont per grens hoeveel laadpunten en laadsessies te lang bezet zijn, gerekend op de eigen meting.</li>
              <li>Onder de regels voor terugkerend staat direct hoeveel laadpunten met de ingevulde waarden terugkerend te lang bezet zijn.</li>
              <li>Pas na Opslaan rekent de module met de nieuwe waarden.</li>
            </ul>
          </div>
          <div style={blok}>
            <span style={label}>Tabblad Trend</span>
            <h4 style={blokKop}>Verloop</h4>
            <ul style={lijstje}>
              <li>Per week, maand of kwartaal hoe de cijfers veranderen.</li>
              <li>Elke periode telt alleen de gegevens van die periode.</li>
              <li>Na een wijziging van de grens of de controle-uren rekent de module alle periodes opnieuw, zodat ze vergelijkbaar blijven.</li>
            </ul>
          </div>
        </div>

        <h3 style={sectie}>Goed om te weten</h3>
        <div style={raster(240)}>
          <div style={blok}>
            <h4 style={blokKop}>Welke laadpunten meetellen</h4>
            <p style={tekst}>Publieke laadpunten voor gewoon laden, volgens de laadpuntenlijst van de Vlaamse overheid. Nieuwe laadpunten van de concessie tellen altijd mee. Laadpunten voor deelauto's tellen niet mee als ze in een ingelezen lijst staan.</p>
            {toegang?.lijstBeschikbaar && gemeente && (
              <p style={{ ...tekst, marginTop: 8 }}>{gemeente} heeft {getal(toegang.mowPubliek)} publieke laadpunten. De module volgt er ongeveer {getal(ongeveer(toegang.publiek))}; van de rest maakt de exploitant de stand niet openbaar.</p>
            )}
          </div>
          <div style={blok}>
            <h4 style={blokKop}>Rotatietarief</h4>
            <p style={tekst}>Op laadpunten van de nieuwe concessie betaalt de bestuurder vanaf {getal(rot.startNaUren ?? 4)} uur na de start {getal(rot.euroPerMinuut ?? 0.05)} euro per minuut, tussen {rotVan} en {rotTot} uur, met een maximum van {getal(rot.maxEuro ?? 50)} euro. Dat geld gaat naar de stad.</p>
            <p style={{ ...tekst, marginTop: 8 }}>Op andere laadpunten geldt het tarief van hun eigen concessie, of geen tarief. Die opbrengst telt niet mee in de raming.</p>
          </div>
          <div style={{ ...blok, background: N.warnBg, borderColor: '#E8D3AC' }}>
            <h4 style={blokKop}>Grenzen van de meting</h4>
            <p style={tekst}>Er wordt elk uur gemeten, dus een duur klopt tot op een uur. De gegevens tonen alleen of een auto aangesloten is, niet of hij nog laadt. Een brandstofauto op een laadplek is niet te zien.</p>
          </div>
        </div>

        <div style={{ marginTop: 22, borderTop: `1px solid ${N.line}`, paddingTop: 14 }}>
          <button type="button" aria-expanded={toonBegrippen} onClick={() => setToonBegrippen(v => !v)}
                  style={{ ...st.knopLicht, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <span aria-hidden="true">{toonBegrippen ? '▾' : '▸'}</span>Begrippen ({begrippen(ins).length})</button>
          {toonBegrippen && (
            <dl style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '10px 24px', margin: '14px 0 0' }}>
              {begrippen(ins).map(([a, b]) => (
                <div key={a} style={{ borderLeft: `3px solid ${N.line2}`, paddingLeft: 10 }}>
                  <dt style={{ fontWeight: 600, fontSize: 14.5, color: N.ink }}>{a}</dt>
                  <dd style={{ ...tekst, fontSize: 14, margin: '2px 0 0' }}>{b}</dd>
                </div>))}
            </dl>)}
        </div>
      </div>
    </div>
  );
}

// De begrippen in de Uitleg. De getallen volgen de instellingen.
function begrippen(ins) {
  const van = ins.controle?.van ?? 8, tot = ins.controle?.tot ?? 20;
  const weken = ins.patroonWeken ?? 4, minimum = ins.patroonMinimum ?? 3, keren = ins.patroonKeren ?? 1;
  const grens = ins.grensLangUren != null ? `${getal(ins.grensLangUren)} uur` : 'de grens';
  return [
    ['Te lang bezet', `Langer dan ${grens} aaneen in gebruik, tussen ${van} en ${tot} uur.`],
    ['Terugkerend', `Minstens ${keren} keer per week te lang bezet, in minstens ${minimum} van de laatste ${weken} weken.`],
    ['Meerdere keren', 'Vaker dan één keer te lang bezet, maar niet terugkerend.'],
    ['Eenmalig', `Eén keer te lang bezet in de laatste ${weken} weken.`],
    ['Uren boven de grens', `Hoeveel uur een laadpunt langer bezet bleef dan ${grens}, opgeteld.`],
    ['Weken te lang bezet', `In hoeveel van de laatste ${weken} weken het laadpunt te lang bezet was.`],
    ['Niet beschikbaar', 'Het laadpunt meldt dat het buiten gebruik is.'],
    ['Controle-uren', 'De uren waarin een laadpunt als te lang bezet kan tellen, zelf in te stellen, bijvoorbeeld 8 tot 20 uur.'],
    ['Keren per week', 'Hoe vaak een laadpunt in een week te lang bezet moet zijn om die week mee te tellen voor terugkerend.'],
    ['Laadsessies langer dan de grens', 'Gemiddeld aantal laadsessies per dag dat langer duurt dan de grens.'],
    ['Drukste moment', 'De weekdag en het uur waarop het vaakst een laadpunt te lang bezet was.'],
    ['Nieuwe concessie', 'Laadpunten die de nieuwe concessiehouder plaatst. Het rotatietarief gaat naar de stad.'],
    ['Bestaande concessie', 'Laadpunten van de lopende concessie. Het rotatietarief gaat naar de exploitant.'],
    ['Vooruitblik', 'Schatting van wat het rotatietarief de stad oplevert als de nieuwe laadpunten net zo gebruikt worden als de bestaande.'],
  ];
}

// ── De kaart ─────────────────────────────────────────────────────────
function Kaart({ zichtbaar, analyse, gekozen }) {
  const ref = useRef(null), kaart = useRef(null), laag = useRef(null), markers = useRef(new Map());
  const getekend = useRef(null);
  useEffect(() => {
    if (!zichtbaar || !ref.current) return;
    try {
      if (!kaart.current) {
        kaart.current = L.map(ref.current, { center: [50.88, 4.70], zoom: 13, zoomControl: false, zoomSnap: 0.25, zoomDelta: 0.5 });
        L.control.zoom({ position: 'bottomleft' }).addTo(kaart.current);
        L.tileLayer(KAART_ONDERGROND, { attribution: '© OpenStreetMap · CARTO', maxZoom: 19 }).addTo(kaart.current);
        L.tileLayer(KAART_NAMEN, { maxZoom: 19 }).addTo(kaart.current);
        laag.current = L.layerGroup().addTo(kaart.current);
      }
      const t = setTimeout(() => kaart.current?.invalidateSize(), 60);
      return () => clearTimeout(t);
    } catch (e) {
      console.error('Kaart van de handhaving niet gemaakt:', e.message);
    }
  }, [zichtbaar]);

  useEffect(() => {
    if (!zichtbaar || !kaart.current || !laag.current || !analyse) return;
    // Alleen opnieuw tekenen als er nieuwe cijfers zijn. Terugkomen op het
    // tabblad laat de kaart staan waar hij stond.
    if (getekend.current === analyse) return;
    const eersteKeer = getekend.current == null;
    getekend.current = analyse;
    try {
      kaart.current.invalidateSize();
      laag.current.clearLayers();
      markers.current = new Map();
      const punten = [];
      for (const p of analyse.alleLaadpunten || []) {
        L.circleMarker([p.lat, p.lng], { radius: 2.5, stroke: false, fillColor: N.ink4, fillOpacity: 0.6, interactive: false })
          .addTo(laag.current);
        punten.push([p.lat, p.lng]);
      }
      for (const p of analyse.laadpunten || []) {
        if (p.lat == null) continue;
        const m = L.circleMarker([p.lat, p.lng], { radius: 5 + 3 * (p.wekenLang || 1), color: '#fff', weight: 1.5,
          fillColor: SOORT_KLEUR[p.soort] || N.accent, fillOpacity: 0.9 })
          .bindPopup(`<div style="font-family:inherit;font-size:14px;line-height:1.45">`
            + `<b>${esc(p.adres || 'Adres onbekend')}</b><br>${esc(p.evse || '')}<br>${esc(p.exploitant)}`
            + `<br>${esc(SOORT_TEKST[p.soort] || '')} te lang bezet · ${p.wekenLang} van ${p.wekenBekeken} weken`
            + `<br>${getal(p.urenBoven)} uur boven de grens</div>`)
          .addTo(laag.current);
        markers.current.set(p.laadpunt, m);
      }
      // De hele gemeente alleen bij de eerste keer, en niet als er via de knop
      // Kaart een laadpunt gekozen is: dan zoomt het effect hieronder in.
      if (eersteKeer && !gekozen && punten.length) kaart.current.fitBounds(L.latLngBounds(punten), { padding: [12, 12] });
    } catch (e) {
      console.error('Laadpunten op de kaart van de handhaving niet getekend:', e.message);
    }
  }, [zichtbaar, analyse, gekozen]);

  useEffect(() => {
    if (!zichtbaar || !gekozen || !kaart.current) return;
    const m = markers.current.get(gekozen);
    if (!m) return;
    // Zonder beweging: een lopende zoombeweging zou de nieuwe plek negeren.
    const t = setTimeout(() => {
      kaart.current.invalidateSize();
      kaart.current.setView(m.getLatLng(), 18, { animate: false });
      m.openPopup();
    }, 80);
    return () => clearTimeout(t);
  }, [zichtbaar, gekozen, analyse]);

  return (
    <div style={st.kaart}>
      <h2 style={st.kaartKop}>Laadpunten die te lang bezet zijn
        <small style={st.klein}>grootte van de stip: aantal weken te lang bezet; de knop Kaart in een tabel springt naar het laadpunt</small></h2>
      <div ref={ref} style={{ height: 520, background: '#F3F1EC' }} />
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', fontSize: 12.5, color: N.ink3, padding: '10px 18px 14px' }}>
        {['terugkerend', 'meerdere', 'eenmalig'].map(s => (
          <span key={s} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 12, height: 12, borderRadius: 999, background: SOORT_KLEUR[s], display: 'inline-block' }} />
            {SOORT_TEKST[s].toLowerCase()} te lang bezet
          </span>
        ))}
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 6, height: 6, borderRadius: 999, background: N.ink4, display: 'inline-block' }} />
          overige gemeten laadpunten
        </span>
      </div>
    </div>
  );
}

// ── Trend ────────────────────────────────────────────────────────────
// Het verloop per week, maand of kwartaal. Elke periode telt alleen de gegevens
// van die periode. De server bewaart afgesloten periodes; de lopende periode
// rekent hij bij elke vraag opnieuw.
const TREND_KC = [
  { id: 'langerPerDag', naam: 'Laadsessies langer dan de grens', eenheid: 'per dag' },
  { id: 'teLang', naam: 'Laadpunten te lang bezet' },
  { id: 'duur', naam: 'Duur overtreding', uur: true },
  { id: 'buitenGebruik', naam: 'Buiten gebruik' },
];
const TREND_TONEN = 8;
function trendAandeel(k, p) {
  if (k.id === 'langerPerDag') return p.aandeelLanger;
  if (k.id === 'teLang') return p.gemeten ? p.teLang / p.gemeten : null;
  if (k.id === 'buitenGebruik') return p.gemeten ? p.buitenGebruik / p.gemeten : null;
  return null;
}
const trendGetal = (k, v) => (v == null ? '–' : k.uur ? Number(v).toLocaleString('nl-BE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : getal(v));
const trendNoem = (l) => (/^\d/.test(l) ? 'het ' + l : l.charAt(0).toLowerCase() + l.slice(1));

function TrendGrafiek({ perioden, k, soort }) {
  const [hover, setHover] = useState(null);
  const W = 760, H = 230, L = 70, R = 40, T = 22, B = 36;
  const punten = perioden.map((p, i) => ({ p, i, v: p[k.id] })).filter(x => x.v != null);
  const max = Math.max(1, ...punten.map(x => x.v)) * 1.25;
  const stap = k.uur ? 1 : max > 300 ? 100 : max > 120 ? 50 : max > 40 ? 10 : 2;
  const top = Math.ceil(max / stap) * stap;
  const x = (i) => L + (W - L - R) * (perioden.length === 1 ? 0.5 : i / (perioden.length - 1));
  const y = (v) => T + (H - T - B) * (1 - v / top);
  const veel = perioden.length > 10;
  const lijnen = [];
  for (let v = 0; v <= top + 1e-9; v += stap) lijnen.push(v);
  const kort = (p) => (soort === 'week' ? (veel ? p.naam.replace('Week ', '') : p.naam.toLowerCase()) : p.naam);
  const tip = hover != null ? punten.find(x => x.i === hover) : null;
  let tipTekst = '', tipX = 0, tipY = 0, tipB = 0;
  if (tip) {
    const a = trendAandeel(k, tip.p);
    tipTekst = `${tip.p.naam}: ${trendGetal(k, tip.v)}${k.uur ? ' uur' : k.eenheid ? ' ' + k.eenheid : ''}${a != null ? ` (${Math.round(a * 100)} %)` : ''}`;
    tipB = tipTekst.length * 6.4 + 18;
    tipX = Math.min(Math.max(x(tip.i) - tipB / 2, 4), W - tipB - 4);
    tipY = Math.max(y(tip.v) - 40, 2);
  }
  return (
    <div style={{ border: `1px solid ${N.line}`, borderRadius: 8, padding: '10px 6px 4px', overflowX: 'auto' }}>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`${k.naam}, per ${soort}`} style={{ display: 'block', width: '100%', minWidth: 420, height: 'auto', fontFamily: FONT }}
           onMouseLeave={() => setHover(null)}>
        {lijnen.map(v => <g key={v}>
          <line x1={L - 30} x2={W - R + 20} y1={y(v)} y2={y(v)} stroke={N.line} strokeWidth="1" />
          <text x={L - 36} y={y(v) + 4} textAnchor="end" fontSize="9" fill={N.ink3}>{getal(v)}</text>
        </g>)}
        {punten.length > 1 && <>
          <path d={`M${x(punten[0].i)},${y(0)} ${punten.map(q => `L${x(q.i)},${y(q.v)}`).join(' ')} L${x(punten[punten.length - 1].i)},${y(0)} Z`} fill={N.accent} opacity="0.08" />
          <path d={`M${punten.map(q => `${x(q.i)},${y(q.v)}`).join(' L')}`} fill="none" stroke={N.accent} strokeWidth="2.5" />
        </>}
        {perioden.map((p, i) => (!veel || i % 2 === 0 || i === perioden.length - 1) && (
          <g key={'l' + p.sleutel}>
            <text x={x(i)} y={H - 18} textAnchor="middle" fontSize="9" fill={N.ink3}>{kort(p)}</text>
            {!veel && p.status && <text x={x(i)} y={H - 5} textAnchor="middle" fontSize="9" fill={N.ink3}>{p.status}</text>}
          </g>))}
        {veel && <text x={(L + W - R) / 2} y={H - 3} textAnchor="middle" fontSize="9" fill={N.ink3}>{soort === 'week' ? 'weeknummer' : ''}</text>}
        {punten.map(q => (
          <g key={'p' + q.p.sleutel} onMouseEnter={() => setHover(q.i)} onFocus={() => setHover(q.i)} onBlur={() => setHover(null)} tabIndex={0}
             aria-label={`${q.p.naam}: ${trendGetal(k, q.v)}`} style={{ cursor: 'default', outline: 'none' }}>
            <circle cx={x(q.i)} cy={y(q.v)} r="12" fill="transparent" />
            <circle cx={x(q.i)} cy={y(q.v)} r={hover === q.i ? 6 : veel ? 3.5 : 4.5}
                    fill={q.p.status ? '#fff' : N.accent} stroke={q.p.status ? N.accent : '#fff'} strokeWidth="2" />
            {(!veel || q.i === perioden.length - 1) && hover !== q.i &&
              <text x={x(q.i)} y={y(q.v) - 10} textAnchor="middle" fontSize="10.5" fontWeight="600" fill={N.ink}>{trendGetal(k, q.v)}</text>}
          </g>))}
        {tip && <g pointerEvents="none">
          <rect x={tipX} y={tipY} width={tipB} height={24} rx={5} fill={N.ink} opacity="0.92" />
          <text x={tipX + tipB / 2} y={tipY + 16} textAnchor="middle" fontSize="11" fill="#fff">{tipTekst}</text>
        </g>}
      </svg>
    </div>
  );
}

function Trend({ gemId, semi, versie, grensGekozen }) {
  const [soort, setSoort] = useState('week');
  const [keuze, setKeuze] = useState('langerPerDag');
  const [alles, setAlles] = useState(false);
  // Opgehaalde cijfers per soort, zolang gemeente, schakelaar en instellingen gelijk blijven.
  const [cache, setCache] = useState({ sleutel: null, perSoort: {} });
  const [fout, setFout] = useState('');
  const [laden, setLaden] = useState(false);
  const sleutel = `${gemId}|${semi}|${versie}`;
  const geldig = cache.sleutel === sleutel;
  const heeft = geldig && !!cache.perSoort[soort];
  useEffect(() => {
    if (!gemId || heeft) return undefined;
    let weg = false;
    setLaden(true); setFout('');
    getHandhavingTrend(gemId, soort, semi)
      .then(r => { if (!weg) setCache(c => ({ sleutel, perSoort: { ...(c.sleutel === sleutel ? c.perSoort : {}), [soort]: r } })); })
      .catch(e => { if (!weg) setFout(e.message); })
      .finally(() => { if (!weg) setLaden(false); });
    return () => { weg = true; };
  }, [gemId, semi, soort, sleutel, heeft]);
  const d = geldig ? cache.perSoort[soort] : null;
  const perioden = d?.perioden || [];
  const k = TREND_KC.find(x => x.id === keuze);
  const seg = (aan) => ({ fontFamily: 'inherit', fontSize: 14, padding: '7px 14px', border: 0, cursor: 'pointer',
                         background: aan ? N.accent : '#fff', color: aan ? '#fff' : N.ink2, fontWeight: aan ? 600 : 400 });
  const chip = (aan) => ({ fontFamily: 'inherit', fontSize: 13.5, padding: '6px 12px', borderRadius: 999, cursor: 'pointer',
                          border: `1px solid ${aan ? N.accent : N.line2}`, background: aan ? N.accent : '#fff', color: aan ? '#fff' : N.ink2 });
  const laatste = perioden[perioden.length - 1], vorige = perioden[perioden.length - 2];
  let samen = null;
  if (laatste && laatste[keuze] != null) {
    const v = laatste[keuze];
    const tekst = { langerPerDag: `${getal(v)} laadsessies per dag langer dan de grens`, teLang: `${getal(v)} laadpunten te lang bezet`,
                    duur: `de overtreding duurt gemiddeld ${trendGetal(k, v)} uur`, buitenGebruik: `${getal(v)} laadpunten buiten gebruik` }[keuze];
    let vgl = '';
    if (vorige && vorige[keuze] != null) {
      const dv = Math.round((v - vorige[keuze]) * 10) / 10;
      const woord = k.uur ? (dv > 0 ? 'langer' : 'korter') : (dv > 0 ? 'meer' : 'minder');
      vgl = dv === 0 ? `, evenveel als in ${trendNoem(vorige.naam)}` : `, ${trendGetal(k, Math.abs(dv))}${k.uur ? ' uur' : ''} ${woord} dan in ${trendNoem(vorige.naam)}`;
    }
    samen = <><b>{laatste.naam}{laatste.status === 'lopend' ? ' tot nu' : ''}:</b> {tekst}{vgl}.</>;
  }
  const rijen = [...perioden].reverse();
  const zicht = alles ? rijen : rijen.slice(0, TREND_TONEN);
  const cel = (kc, p) => {
    const v = p[kc.id], a = trendAandeel(kc, p);
    return (
      <td key={kc.id} style={{ ...tdG, whiteSpace: 'nowrap' }}>
        <span style={{ fontWeight: 600 }}>{trendGetal(kc, v)}</span>
        {v != null && (kc.uur || kc.eenheid) && <span style={{ fontSize: 13, color: N.ink3 }}> {kc.uur ? 'uur' : kc.eenheid}</span>}
        {v != null && a != null && <span style={{ fontSize: 13.5, color: N.ink3 }}> ({Math.round(a * 100)} %)</span>}
      </td>);
  };
  const pilStatus = { display: 'inline-block', marginLeft: 6, fontSize: 11.5, padding: '1px 8px', borderRadius: 999, fontWeight: 500,
                      background: '#F7EFDC', color: N.ink2, border: `1px solid ${N.line}` };
  return (
    <div style={st.kaart}>
      <h2 style={st.kaartKop}>Verloop</h2>
      <div style={{ padding: '16px 18px 18px', display: 'grid', gap: 14 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between' }}>
          <p style={{ margin: 0, color: N.ink2, fontSize: 14, lineHeight: 1.55 }}>
            {d ? <>Gerekend met de huidige instellingen: grens {d.grens != null ? `${getal(d.grens)} uur` : 'nog niet gekozen'}, controle-uren {d.controle?.van} tot {d.controle?.tot} uur.</> : ' '}</p>
          <div role="group" aria-label="Periode" style={{ display: 'inline-flex', border: `1px solid ${N.line2}`, borderRadius: 8, overflow: 'hidden' }}>
            {[['week', 'Per week'], ['maand', 'Per maand'], ['kwartaal', 'Per kwartaal']].map(([id, naam], i) => (
              <button key={id} type="button" aria-pressed={soort === id} onClick={() => { setSoort(id); setAlles(false); }}
                      style={{ ...seg(soort === id), borderLeft: i ? `1px solid ${N.line2}` : 0 }}>{naam}</button>))}
          </div>
        </div>
        {!grensGekozen && <div style={{ ...st.melding, marginTop: 0 }}>Kies bij Instellingen de grens voor te lang bezet. Tot dan is er niets te lang bezet.</div>}
        {fout && <div role="alert" style={{ ...st.fout, marginTop: 0 }}>{fout}</div>}
        {laden && !d && <div style={{ color: N.ink3, fontSize: 14 }}>Bezig met rekenen…</div>}
        {d && !perioden.length && <div style={{ color: N.ink3, fontSize: 14 }}>Voor deze gemeente is nog niet gemeten.</div>}
        {perioden.length > 0 && <>
          <div role="group" aria-label="Cijfer in de grafiek" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {TREND_KC.map(kc => <button key={kc.id} type="button" aria-pressed={keuze === kc.id} onClick={() => setKeuze(kc.id)} style={chip(keuze === kc.id)}>{kc.naam}</button>)}
          </div>
          {samen && <div style={{ padding: '10px 12px', borderRadius: 8, background: N.head, border: `1px solid ${N.line}`, fontSize: 15 }}>{samen}</div>}
          <TrendGrafiek perioden={perioden} k={k} soort={soort} />
          <div style={{ overflowX: 'auto', border: `1px solid ${N.line}`, borderRadius: 8 }}>
            <table style={st.table}>
              <thead><tr><th style={st.th}>Periode</th>{TREND_KC.map(kc => <th key={kc.id} style={{ ...st.th, textAlign: 'right' }}>{kc.naam}</th>)}</tr></thead>
              <tbody>
                {zicht.map(p => (
                  <tr key={p.sleutel}>
                    <td style={{ ...st.td, whiteSpace: 'nowrap' }}>{p.naam}{p.status && <span style={pilStatus}>{p.status}</span>}
                      {p.datum && <span style={{ display: 'block', fontSize: 12.5, color: N.ink3 }}>{p.datum}</span>}</td>
                    {TREND_KC.map(kc => cel(kc, p))}
                  </tr>))}
              </tbody>
            </table>
          </div>
          {rijen.length > TREND_TONEN && (
            <button type="button" style={{ ...st.knopLicht, justifySelf: 'start' }} onClick={() => setAlles(a => !a)}>
              {alles ? `Alleen de laatste ${TREND_TONEN} tonen` : `Alle ${rijen.length} tonen`}</button>)}
          <div style={{ display: 'grid', gap: 3, fontSize: 12.5, color: N.ink3, lineHeight: 1.5 }}>
            <div><b style={{ color: N.ink2 }}>Laadsessies langer dan de grens:</b> gemiddeld per dag.</div>
            <div><b style={{ color: N.ink2 }}>Laadpunten te lang bezet:</b> minstens één keer langer bezet dan de grens in die periode.</div>
            <div><b style={{ color: N.ink2 }}>Duur overtreding:</b> gemiddeld, in uren boven de grens.</div>
            <div><b style={{ color: N.ink2 }}>Buiten gebruik:</b> laadpunten die op de laatste dag van de periode al meer dan een dag buiten gebruik zijn.</div>
            <div><b style={{ color: N.ink2 }}>Percentage:</b> aandeel van alle laadsessies per dag, of van alle gemeten {semi ? 'publieke en semi-publieke' : 'publieke'} laadpunten in die periode.</div>
          </div>
        </>}
      </div>
    </div>
  );
}

// ── Hulp bij kiezen grens ────────────────────────────────────────────
// Een venster op tabblad Instellingen. Alle cijfers komen van de server
// (grenshulp in handhavinganalyse.js), gerekend met de controle-uren en de
// regels voor terugkerend zoals ze op dat moment ingevuld zijn.
const GRENS_VAST = [4, 5, 6, 7, 8];
const GRENS_ADVIES = 6;
const deelVan = (n, tot) => (tot ? `${Math.round(100 * n / tot)} %` : '');
function periodeTekst(van, tot) {
  const a = datumTekst(van), b = datumTekst(tot);
  const [da, ...ra] = a.split(' '), rb = b.split(' ').slice(1);
  return ra.join(' ') === rb.join(' ') ? `${da} tot en met ${b}` : `${a} tot en met ${b}`;
}
const PRULLENBAK = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 6h18" /><path d="M8 6V4h8v2" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" />
  </svg>
);

function GrensHulp({ hulp, hulpFout, grens, regel, semi, rotatieStart, onKies, onSluit }) {
  const [extra, setExtra] = useState(null);
  const [vrij, setVrij] = useState('');
  const [vrijFout, setVrijFout] = useState('');
  useEffect(() => {
    const t = (e) => { if (e.key === 'Escape') onSluit(); };
    document.addEventListener('keydown', t);
    return () => document.removeEventListener('keydown', t);
  }, [onSluit]);
  const h = hulp;
  const van = h?.controle?.van ?? 8, tot = h?.controle?.tot ?? 20;
  const max = h?.maxGrens ?? 0;
  const huidig = Number(String(grens).replace(',', '.'));
  const lijst = GRENS_VAST.filter(g => g <= max);
  if (extra != null && extra <= max && !lijst.includes(extra)) lijst.push(extra);
  lijst.sort((x, y) => x - y);
  const p = { color: N.ink2, lineHeight: 1.6, margin: '12px 0 0', fontSize: 15 };
  const thW = { ...st.th, textAlign: 'right', whiteSpace: 'normal', lineHeight: 1.35, verticalAlign: 'bottom', width: 190 };
  const tdW = { ...st.td, textAlign: 'right', whiteSpace: 'nowrap' };
  const pctSt = { color: N.ink3, fontSize: 13.5 };
  const pil = (soort) => ({ display: 'inline-block', marginLeft: 8, fontSize: 12, padding: '2px 8px', borderRadius: 999, fontWeight: 500,
                            background: soort === 'h' ? N.accent : N.goud, color: soort === 'h' ? '#fff' : N.ink });
  const bereken = () => {
    const n = Number(String(vrij).trim().replace(',', '.'));
    if (!Number.isInteger(n) || n < 1 || n > max) {
      setVrijFout(`Kies een heel aantal uren van 1 tot en met ${max}. De controle-uren lopen van ${van} tot ${tot} uur; bij een grens van ${max + 1} uur of meer is een laadpunt nooit te lang bezet.`);
      return;
    }
    setVrijFout(''); setExtra(n);
  };
  const gHuidig = Number.isFinite(huidig) && huidig >= 1 ? Math.floor(huidig) : null;
  const cHuidig = gHuidig != null ? h?.grenzen?.[gHuidig] : null;
  return (
    <div onClick={onSluit} style={{ position: 'fixed', inset: 0, background: 'rgba(18,22,28,.55)', display: 'flex',
      alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto', zIndex: 2000 }}>
      <div role="dialog" aria-modal="true" aria-labelledby="grenshulp-titel" onClick={e => e.stopPropagation()}
           style={{ background: '#fff', borderRadius: 12, maxWidth: 940, width: '100%', padding: '24px 26px', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <h3 id="grenshulp-titel" style={{ margin: 0, fontSize: 21 }}>Hulp bij het kiezen van de grens</h3>
          <button type="button" style={st.knopLicht} onClick={onSluit}>Sluiten</button>
        </div>
        <p style={p}>Hoe hoger de grens, hoe zekerder het gaat om parkeren en niet om laden. Hoe lager de grens, hoe meer laadpunten te lang bezet zijn.</p>
        <p style={p}><b>Te lang bezet</b> betekent dat een EV langer aan de laadpaal staat dan de ingestelde urengrens. Er wordt gemeten tussen {van} en {tot} uur. Achter elk aantal staat het aandeel van het totaal.</p>
        <p style={p}>Stond een EV er al voor {van} uur, dan telt de duur vanaf {van} uur.</p>
        {hulpFout && <div role="alert" style={st.fout}>{hulpFout}</div>}
        {!h && !hulpFout && <p style={{ ...p, color: N.ink3 }}>Bezig met rekenen…</p>}
        {h && <>
          <div style={{ overflowX: 'auto', marginTop: 16, border: `1px solid ${N.line}`, borderRadius: 8 }}>
            <table style={st.table}>
              <thead><tr>
                <th style={{ ...st.th, verticalAlign: 'bottom' }}>Grens</th>
                <th style={thW}>Laadpunten terugkerend te lang bezet</th>
                <th style={thW}>Laadpunten minstens één keer te lang bezet</th>
                <th style={thW}>Laadsessies per dag langer dan de grens</th>
                <th style={{ ...st.th, width: 90 }} />
              </tr></thead>
              <tbody>
                {lijst.map(g => {
                  const c = h.grenzen[g];
                  const isHuidig = g === huidig;
                  const bg = isHuidig ? '#EAF2F4' : g === GRENS_ADVIES ? '#F7EFDC' : undefined;
                  const cel = { ...st.td, background: bg, whiteSpace: 'nowrap' };
                  return (
                    <tr key={g}>
                      <td style={cel}>{g} uur
                        {!GRENS_VAST.includes(g) && (
                          <button type="button" aria-label={`Regel ${g} uur verwijderen`} title="Verwijderen"
                            onClick={() => { setExtra(null); setVrij(''); }}
                            style={{ border: 0, background: 'none', color: N.ink3, cursor: 'pointer', padding: '2px 4px', marginLeft: 6, verticalAlign: -3 }}>
                            {PRULLENBAK}</button>)}
                        {isHuidig && <span style={pil('h')}>huidige grens</span>}
                        {g === GRENS_ADVIES && <span style={pil('a')}>advies</span>}
                      </td>
                      <td style={{ ...tdW, background: bg }}>{getal(c.terugkerend)} <span style={pctSt}>({deelVan(c.terugkerend, h.laadpunten)})</span></td>
                      <td style={{ ...tdW, background: bg }}>{getal(c.minstensEens)} <span style={pctSt}>({deelVan(c.minstensEens, h.laadpunten)})</span></td>
                      <td style={{ ...tdW, background: bg }}>{getal(c.langerPerDag)} <span style={pctSt}>({deelVan(c.langerPerDag, h.sessiesPerDag)})</span></td>
                      <td style={{ ...tdW, background: bg }}><button type="button" style={st.knopLicht} onClick={() => onKies(g)}>Kies</button></td>
                    </tr>);
                })}
              </tbody>
            </table>
          </div>
          <dl style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '4px 12px', margin: '12px 0 0', fontSize: 13.5, color: N.ink2 }}>
            <dt style={{ fontWeight: 600, color: N.ink }}>Laadpunten terugkerend te lang bezet</dt>
            <dd style={{ margin: 0 }}>minstens {regel.keren} keer per week langer bezet dan de grens, in minstens {regel.minimum} van de laatste {regel.weken} weken. Alleen deze kolom verandert met de instellingen bij Terugkerend te lang bezet.</dd>
            <dt style={{ fontWeight: 600, color: N.ink }}>Laadpunten minstens één keer te lang bezet</dt>
            <dd style={{ margin: 0 }}>in de meetperiode minstens één keer langer bezet dan de grens.</dd>
            <dt style={{ fontWeight: 600, color: N.ink }}>Laadsessies per dag langer dan de grens</dt>
            <dd style={{ margin: 0 }}>gemiddelde per dag over de meetperiode.</dd>
            <dt style={{ fontWeight: 600, color: N.ink }}>Percentage</dt>
            <dd style={{ margin: 0 }}>aandeel van alle gemeten {semi ? 'publieke en semi-publieke' : 'publieke'} laadpunten, of van alle laadsessies per dag.</dd>
          </dl>
          <div style={{ marginTop: 14, padding: '12px 14px', border: `1px solid ${N.line2}`, borderRadius: 8, background: N.head, lineHeight: 1.6, fontSize: 14.5, color: N.ink2 }}>
            <b style={{ display: 'block', color: N.ink, marginBottom: 4 }}>Extra inzicht</b>
            <p style={{ margin: 0 }}>In de meetperiode van {periodeTekst(h.periode.van, h.periode.tot)} waren er op de {semi ? 'publieke en semi-publieke' : 'publieke'} laadpunten gemiddeld {getal(h.sessiesPerDag)} laadsessies per dag tussen {van} en {tot} uur. Gemiddeld {getal(h.alBezigPerDag)} daarvan ({deelVan(h.alBezigPerDag, h.sessiesPerDag)}) liepen al voor {van} uur. Die zijn op verschillende momenten begonnen: {h.herkomst.dagErvoor} % de dag ervoor, {h.herkomst.naMiddernacht} % na middernacht en {h.herkomst.kortVoorBegin} % in de twee uur voor {van} uur.</p>
            {cHuidig && (
              <p style={{ margin: '8px 0 0' }}>Bij de huidige grens van {getal(huidig)} uur duren gemiddeld {getal(cHuidig.langerPerDag)} laadsessies per dag langer dan de grens. {getal(cHuidig.alBezigPerDag)} daarvan ({deelVan(cHuidig.alBezigPerDag, cHuidig.langerPerDag)} van die {getal(cHuidig.langerPerDag)}) liepen al voor {van} uur.</p>
            )}
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 16 }}>
            <div>
              <label htmlFor="grenshulp-vrij" style={st.label}>Andere grens (uren)</label>
              <input id="grenshulp-vrij" type="text" inputMode="numeric" placeholder="bijv. 9" value={vrij}
                     onChange={e => setVrij(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') bereken(); }}
                     style={{ ...st.input, width: 110 }} />
            </div>
            <button type="button" style={st.knopLicht} onClick={bereken}>Bereken</button>
          </div>
          {vrijFout && <div style={{ color: N.red, fontSize: 13.5, marginTop: 8 }}>{vrijFout}</div>}
          {GRENS_ADVIES <= max && (
            <div style={{ marginTop: 16, padding: '12px 14px', borderRadius: 8, background: '#F7EFDC', color: N.ink, lineHeight: 1.55, fontSize: 14.5 }}>
              <b>Advies: {GRENS_ADVIES} uur.</b> Een gemiddelde auto laden van 20 naar 80 procent duurt ruim 3 uur aan een paal van 11 kW en bijna 5 uur aan een paal van 7 kW. Omdat er per uur gemeten wordt, stond een auto bij een grens van {GRENS_ADVIES} uur er echt minstens {GRENS_ADVIES} uur. Dan is hij vrijwel zeker vol.
            </div>
          )}
          <div style={{ fontSize: 12.5, color: N.ink3, lineHeight: 1.5, marginTop: 12 }}>
            Gemeten over de laatste {getal(h.periode.dagen)} dagen, {semi ? 'publieke en semi-publieke laadpunten' : 'alleen publieke laadpunten'}. Het rotatietarief staat hier los van en gaat in na {getal(rotatieStart ?? 4)} uur.
          </div>
        </>}
      </div>
    </div>
  );
}

// ── Het scherm ───────────────────────────────────────────────────────
const TABS = [['overzicht', 'Overzicht'], ['trend', 'Trend'], ['kaart', 'Kaart'], ['laadpunten', 'Laadpunten'], ['rooster', 'Rooster'],
              ['exploitanten', 'Exploitanten'], ['raming', 'Raming'], ['inlezen', 'Gegevens inlezen'], ['instellingen', 'Instellingen']];

const PCB = 'Public Charging Belgium (TotalEnergies)';
// 2026-12-01 naar 1-12-2026.
function datumKort(iso) {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${Number(m[3])}-${Number(m[2])}-${m[1]}` : '';
}

function instellingenNaarVelden(ins) {
  return {
    controle: ins?.controle ? `${ins.controle.van}-${ins.controle.tot}` : '8-20',
    grens: ins?.grensLangUren ?? '', weken: ins?.patroonWeken ?? '', minimum: ins?.patroonMinimum ?? '',
    keren: ins?.patroonKeren ?? 1,
    rotatieStart: ins?.rotatie?.startNaUren ?? '', rotatieUren: ins?.rotatie ? `${ins.rotatie.van}-${ins.rotatie.tot}` : '',
    boete: ins?.boete ?? '', laadpuntenPerRonde: ins?.laadpuntenPerRonde ?? '', kansBoete: ins?.kansBoete ?? '', wekenControle: ins?.wekenControle ?? '',
    nieuweLaadpunten: ins?.nieuweLaadpunten ?? '',
    boeteNaarGemeente: !!ins?.boeteNaarGemeente,
    rotatieTarief: ins?.rotatie?.euroPerMinuut != null ? String(ins.rotatie.euroPerMinuut).replace('.', ',') : '',
    rotatieMax: ins?.rotatie?.maxEuro != null ? String(ins.rotatie.maxEuro).replace('.', ',') : '',
    concessieStart: datumKort(ins?.concessie?.start),
    concessieHouder: ins?.concessie?.houder ?? PCB,
    wijkenUit: (ins?.wijkenUit || []).map(String),
  };
}

export default function Handhaving({ onHoofdmenu }) {
  const [gemeenten, setGemeenten] = useState(null);
  const [gemId, setGemId] = useState('');
  const [data, setData] = useState(null);
  const [laden, setLaden] = useState(true);
  const [fout, setFout] = useState('');
  const [tab, setTab] = useState('overzicht');
  const [gekozen, setGekozen] = useState(null);
  const [toonUitleg, setToonUitleg] = useState(false);
  const [velden, setVelden] = useState(instellingenNaarVelden(null));
  const [melding, setMelding] = useState('');
  const [bestanden, setBestanden] = useState([]);
  const [soort, setSoort] = useState('fluvius');
  const [bezig, setBezig] = useState('');
  const [ookSemi, setOokSemi] = useState(false);
  const [ookEenmalig, setOokEenmalig] = useState(false);
  const [alleenStad, setAlleenStad] = useState(false);
  const [roosterWijk, setRoosterWijk] = useState('');
  const [open, setOpen] = useState(() => new Set());
  const [hulp, setHulp] = useState(null);
  const [hulpFout, setHulpFout] = useState('');
  const [toonGrensHulp, setToonGrensHulp] = useState(false);
  const [grensGezet, setGrensGezet] = useState('');
  const bestandRef = useRef(null);

  useEffect(() => {
    getHandhavingGemeenten()
      .then(r => {
        const aan = (r?.gemeenten || []).filter(g => g.aan);
        setGemeenten(aan);
        if (aan.length) setGemId(aan[0].id);
        else setLaden(false);
      })
      .catch(e => { setGemeenten([]); setFout(e.message); setLaden(false); });
  }, []);

  const laad = useCallback(async (id, semi) => {
    if (!id) return;
    setLaden(true); setFout('');
    try {
      const [d, b] = await Promise.all([getHandhavingAnalyse(id, semi), getHandhavingBestanden(id)]);
      setData(d);
      setVelden(instellingenNaarVelden(d.instellingen));
      setBestanden(b?.bestanden || []);
    } catch (e) {
      setFout(e.message);
    } finally {
      setLaden(false);
    }
  }, []);
  useEffect(() => { laad(gemId, ookSemi); }, [gemId, ookSemi, laad]);

  // Cijfers voor het effect van de instellingen en het venster Hulp bij kiezen
  // grens. Opnieuw opgehaald zodra de controle-uren of de regels voor
  // terugkerend veranderen, met een korte pauze zodat niet elke toetsaanslag
  // een berekening start.
  const controleGeldig = /^\s*\d{1,2}\s*-\s*\d{1,2}\s*$/.test(String(velden.controle || ''))
    && (() => { const [v, t] = String(velden.controle).split('-').map(x => Number(x.trim())); return v >= 0 && t <= 24 && t - v >= 2; })();
  useEffect(() => {
    if (tab !== 'instellingen' || !gemId || !data?.meting?.laatste) return undefined;
    if (!controleGeldig) { setHulp(null); setHulpFout(''); return undefined; }
    let weg = false;
    const t = setTimeout(async () => {
      try {
        const r = await getHandhavingGrenshulp(gemId, { controle: String(velden.controle).replace(/\s/g, ''), weken: velden.weken,
                                                         minimum: velden.minimum, keren: velden.keren }, ookSemi);
        if (!weg) { setHulp(r.hulp); setHulpFout(''); }
      } catch (e) {
        if (!weg) { setHulp(null); setHulpFout(e.message); }
      }
    }, 400);
    return () => { weg = true; clearTimeout(t); };
  }, [tab, gemId, ookSemi, data, controleGeldig, velden.controle, velden.weken, velden.minimum, velden.keren]);

  const a = data?.analyse || null;
  const k = a?.kerncijfers;
  const grensGekozen = !!a?.grensGekozen;
  const zichtbaar = (lijst) => (lijst || []).filter(p => ookEenmalig || p.soort !== 'eenmalig');
  const topLocaties = perLocatie(zichtbaar(a?.top));
  const alleLocaties = perLocatie(zichtbaar(a?.laadpunten).filter(p => !alleenStad || p.stad));
  const wissel = (sl) => setOpen(o => { const n = new Set(o); if (n.has(sl)) n.delete(sl); else n.add(sl); return n; });

  const naarKaart = (laadpunt) => { setGekozen(laadpunt); setTab('kaart'); window.scrollTo(0, 0); };
  const download = async (soortBestand, extra) => {
    setBezig(soortBestand); setFout('');
    try { await downloadHandhaving(gemId, soortBestand, extra, ookSemi); }
    catch (e) { setFout(e.message); }
    finally { setBezig(''); }
  };

  const bewaar = async (wat) => {
    setMelding(''); setFout('');
    const [rv, rt] = String(velden.rotatieUren || '').split('-').map(x => x.trim());
    const lichaam = wat === 'raming'
      ? { boete: String(velden.boete), laadpunten_per_ronde: String(velden.laadpuntenPerRonde), kans_boete: String(velden.kansBoete), weken_controle: String(velden.wekenControle),
          nieuwe_laadpunten: String(velden.nieuweLaadpunten), boete_naar_gemeente: velden.boeteNaarGemeente ? '1' : '0' }
      : { controle: String(velden.controle), grens: String(velden.grens === '' ? 'leeg' : velden.grens),
          weken: String(velden.weken), minimum: String(velden.minimum), keren: String(velden.keren), rotatie_start: velden.rotatieStart,
          rotatie_van: rv ?? '', rotatie_tot: rt ?? '',
          rotatie_tarief: String(velden.rotatieTarief), rotatie_max: String(velden.rotatieMax),
          concessie_start: String(velden.concessieStart), concessie_houder: String(velden.concessieHouder || ''),
          wijken_uit: velden.wijkenUit };
    try {
      await bewaarHandhavingInstellingen(gemId, lichaam);
      setMelding('Opgeslagen.');
      setGrensGezet('');
      await laad(gemId, ookSemi);
    } catch (e) {
      setFout(e.message);
    }
  };

  const leesIn = async (bestand) => {
    if (!bestand) return;
    setBezig('inlezen'); setFout(''); setMelding('');
    try {
      const r = await leesHandhavingBestandIn(gemId, soort, bestand);
      setMelding(`Ingelezen: ${getal(r.bestand.regels)} regels, waarvan ${getal(r.bestand.gekoppeld)} gekoppeld aan een gemeten laadpunt.`);
      const b = await getHandhavingBestanden(gemId);
      setBestanden(b?.bestanden || []);
    } catch (e) {
      setFout(e.message);
    } finally {
      setBezig('');
      if (bestandRef.current) bestandRef.current.value = '';
    }
  };

  const kaartKnop = (p) => <button type="button" style={st.knopLicht} onClick={() => naarKaart(p.laadpunt)}>Kaart</button>;
  const eenmaligSchakel = (
    <label style={st.schakelKop}>
      <input type="checkbox" checked={ookEenmalig} onChange={e => setOokEenmalig(e.target.checked)} />
      Ook eenmalig te lang bezet tonen
    </label>
  );
  // Eén regel per locatie; een klik toont de EVSE-nummers eronder.
  // soortTabel 'top' toont het drukste moment, 'alle' de uren en de soort.
  const locatieRijen = (groepen, soortTabel, kolommen) => {
    if (!groepen.length) {
      return [<tr key="leeg"><td colSpan={kolommen} style={{ ...st.td, color: N.ink3 }}>
        {alleenStad && soortTabel === 'alle' ? 'Geen te lang bezette laadpunten met rotatietarief voor de stad.'
          : a && !ookEenmalig && (a.laadpunten || []).length
          ? 'Geen laadpunten die terugkerend of meerdere keren te lang bezet zijn.' : ''}</td></tr>];
    }
    const rijen = [];
    for (const g of groepen) {
      const sl = `${soortTabel}|${g.sleutel}`;
      const isOpen = open.has(sl);
      rijen.push(
        <tr key={sl} onClick={() => wissel(sl)} style={{ cursor: 'pointer' }}>
          <td style={st.td}><span style={{ display: 'inline-block', width: 16, color: N.ink3 }}>{isOpen ? '▾' : '▸'}</span>{g.adres || ''}</td>
          <td style={st.td}>{g.exploitant}</td>
          <td style={st.td}>{g.wijk || ''}</td>
          <td style={tdG}>{aantalTekst(g.punten.length)}</td>
          {soortTabel === 'alle' && <td style={tdG}>{getal(g.urenBoven)}</td>}
          <td style={tdG}>{g.wekenLang} van {g.wekenBekeken}</td>
          {soortTabel === 'top'
            ? <td style={st.td}>{g.drukste}</td>
            : <td style={st.td}><span style={st.pil(g.soort)}>{SOORT_TEKST[g.soort]}</span></td>}
          {soortTabel === 'alle' && <td style={st.td}>{g.rotatietarief}</td>}
          <td style={st.td} onClick={e => e.stopPropagation()}>{kaartKnop(g.punten[0])}</td>
        </tr>);
      if (isOpen) {
        rijen.push(
          <tr key={sl + '|open'}><td colSpan={kolommen} style={{ ...st.td, background: N.paper2, padding: '8px 12px 12px 40px' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: N.ink3, margin: '4px 0 6px' }}>EVSE-nummers op deze locatie</div>
            <table style={st.table}><tbody>
              {g.punten.map(p => (
                <tr key={p.laadpunt}>
                  <td style={st.td}>{p.evse || ''}</td>
                  <td style={tdG}>{getal(p.urenBoven)} uur boven de grens</td>
                  <td style={tdG}>{p.wekenLang} van {p.wekenBekeken} weken</td>
                  <td style={st.td}><span style={st.pil(p.soort)}>{SOORT_TEKST[p.soort]}</span></td>
                  {soortTabel === 'alle' && <td style={st.td}>{p.rotatietarief || ''}</td>}
                  <td style={st.td}>{kaartKnop(p)}</td>
                </tr>))}
            </tbody></table>
          </td></tr>);
      }
    }
    return rijen;
  };
  const veld = (id, label, waarde, zet, hulp, placeholder) => (
    <div>
      <label htmlFor={id} style={st.label}>{label}</label>
      <input id={id} type="text" value={waarde} placeholder={placeholder || ''} onChange={e => zet(e.target.value)} style={st.input} />
      {hulp && <div style={st.hulp}>{hulp}</div>}
    </div>
  );
  const zetVeld = (naam) => (v) => setVelden(o => ({ ...o, [naam]: v }));
  const sluitGrensHulp = useCallback(() => setToonGrensHulp(false), []);
  const soortNamen = { fluvius: 'Gebruiksdata per laadpaal van Fluvius', sessies: 'Sessiegegevens van een exploitant', mow: 'Maandcijfers per laadpunt van MOW',
                       concessie: 'Laadpunten van de nieuwe concessie', deelauto: "Laadpunten voor deelauto's" };
  const wijkAan = (id) => !velden.wijkenUit.includes(String(id));
  const zetWijk = (id, aan) => setVelden(o => {
    const uit = new Set(o.wijkenUit);
    if (aan) uit.delete(String(id)); else uit.add(String(id));
    return { ...o, wijkenUit: [...uit] };
  });
  const houderKeuzes = [PCB, ...(data?.exploitantNamen || [])];
  if (velden.concessieHouder && !houderKeuzes.includes(velden.concessieHouder)) houderKeuzes.push(velden.concessieHouder);

  return (
    <div style={st.pagina}>
      <div style={st.top}>
        <div style={st.logo}>LaadAtlas</div>
        <button type="button" style={st.tknop} onClick={onHoofdmenu}>Hoofdmenu</button>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button type="button" style={st.tknop} disabled={!grensGekozen || !!bezig} onClick={() => download('excel')}>
            {bezig === 'excel' ? 'Bezig…' : 'Excel'}</button>
          <button type="button" style={st.tknop} disabled={!grensGekozen || !!bezig} onClick={() => download('word')}>
            {bezig === 'word' ? 'Bezig…' : 'Rapport (Word)'}</button>
        </div>
      </div>

      <div style={{ ...st.breed, paddingTop: 26 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={st.eyebrow}>Monitoring Module Handhaving</div>
          <button type="button" style={st.knopLicht} onClick={() => setToonUitleg(true)}>Uitleg / Werkwijze</button>
        </div>
        <h1 style={st.h1}>Patronen in het gebruik van laadpunten
          {gemeenten?.length > 0 && (
            <select aria-label="Gemeente" style={st.select} value={gemId} onChange={e => { setGekozen(null); setRoosterWijk(''); setGemId(e.target.value); }}>
              {gemeenten.map(g => <option key={g.id} value={g.id}>{g.naam}</option>)}
            </select>
          )}
        </h1>
        {gemeenten && !gemeenten.length && (
          <div style={st.melding}>Voor geen enkele gemeente staat de meting aan.</div>
        )}
        {data?.meting?.laatste && (
          <div style={st.meetband}>
            <span>Meting sinds <b>{datumTekst(data.meting.sinds)}</b></span>
            <span><b>{getal(data.meting.laadpunten)}</b> gemeten laadpunten{data.toegang?.lijstBeschikbaar
              ? <>, waarvan <b>{getal(data.toegang.publiek)}</b> publiek</> : null}</span>
            {data.concessie && <span><b>{getal(data.concessie.nieuw)}</b> laadpunten van de nieuwe concessie</span>}
            <span><b>{getal(data.meting.exploitanten)}</b> exploitanten</span>
            <span>Laatste meting <b>{datumTekst(data.meting.laatste)}, {uurTekst(data.meting.laatste)}</b></span>
            <span>Periode <b>laatste {data.instellingen.patroonWeken} weken</b></span>
          </div>
        )}
        {data && !data.meting?.laatste && <div style={st.melding}>Voor deze gemeente is nog niet gemeten.</div>}
        {a && !grensGekozen && (
          <div style={st.melding}>Kies bij Instellingen de grens voor te lang bezet. Tot dan is er niets te lang bezet en blijven de tabellen leeg.</div>
        )}
        {data?.adressen && data.adressen.aantal === 0 && (
          <div style={st.melding}>{data.adressen.ophaal && !data.adressen.ophaal.gelukt
            ? `De adressen uit het Adressenregister ophalen is mislukt: ${data.adressen.ophaal.melding || 'onbekende fout'}.`
            : 'De adressen uit het Adressenregister zijn nog niet opgehaald. Tot dan blijft de kolom Adres leeg.'}</div>
        )}
        {fout && <div role="alert" style={st.fout}>{fout}</div>}
        {melding && <div style={{ ...st.melding, background: '#EAF2F4', borderColor: '#C9DDE1', color: N.accent }}>{melding}</div>}
        {laden && <div style={{ ...st.meetband, color: N.ink3 }}>Laden…</div>}

        {data?.toegang?.lijstBeschikbaar && (
          <label style={st.schakel}>
            <input type="checkbox" checked={ookSemi} onChange={e => { setGekozen(null); setOokSemi(e.target.checked); }} />
            Ook semi-publieke laadpunten tonen
          </label>
        )}

        <div style={st.tabs} role="tablist">
          {TABS.map(([id, naam]) => (
            <button key={id} type="button" role="tab" aria-selected={tab === id} style={st.tab(tab === id)}
              onClick={() => { if (id === 'kaart') setGekozen(null); setTab(id); }}>{naam}</button>
          ))}
        </div>
      </div>

      <main style={{ ...st.breed, paddingTop: 22, paddingBottom: 40 }}>
        {/* ── OVERZICHT ── */}
        {tab === 'overzicht' && (
          <section>
            <div style={st.tegels}>
              <div style={st.tegel}><div style={st.tegelN}>{a ? getal(k.terugkerend) : '–'}</div><div style={st.tegelL}>laadpunten terugkerend te lang bezet</div></div>
              <div style={st.tegel}><div style={st.tegelN}>{a ? getal(k.eenmalig) : '–'}</div><div style={st.tegelL}>laadpunten eenmalig te lang bezet</div></div>
              <div style={st.tegel}><div style={st.tegelN}>{a && k.extraPerKeer != null ? `${getal(k.extraPerKeer)} uur` : '–'}</div>
                <div style={st.tegelL}>hoe lang duurt gemiddeld de overtreding</div></div>
              <div style={st.tegel}><div style={st.tegelN}>{a ? getal(k.buitenGebruik) : '–'}</div><div style={st.tegelL}>laadpunten al meer dan een dag buiten gebruik</div></div>
              <div style={st.tegel}><div style={st.tegelN}>{a ? getal(k.metRotatieStad) : '–'}</div><div style={st.tegelL}>laadpunten met rotatietarief voor de stad</div></div>
            </div>

            <div style={st.kaart}>
              <h2 style={st.kaartKop}>Per wijk <small style={st.klein}>gesorteerd op uren boven de grens per laadpunt</small></h2>
              <Tabel kop={[{ t: 'Wijk' }, { t: 'Gemeten laadpunten', g: 1 }, { t: 'Terugkerend te lang bezet', g: 1 }, { t: 'Uren boven de grens', g: 1 }, { t: 'Gemiddeld per laadpunt', g: 1 }, { t: 'Drukste moment' }, { t: 'Rotatietarief' }]}>
                {(a?.perWijk || []).map(w => (
                  <tr key={w.wijk}><td style={st.td}>{w.wijk}</td><td style={tdG}>{getal(w.gemeten)}</td><td style={tdG}>{getal(w.terugkerend)}</td>
                    <td style={tdG}>{getal(w.urenBoven)}</td><td style={tdG}>{getal(w.perLaadpunt)}</td><td style={st.td}>{w.drukste || ''}</td>
                    <td style={st.td}>{w.rotatietarief || ''}</td></tr>
                ))}
              </Tabel>
              <div style={st.voet}>Gemiddeld per laadpunt: de uren boven de grens gedeeld door alle gemeten laadpunten in de wijk, ook die waar niemand te lang stond. Te lang bezet: een laadpunt staat binnen de controle-uren ({data?.instellingen?.controle?.van ?? 8} tot {data?.instellingen?.controle?.tot ?? 20} uur) langer aaneen in gebruik dan de grens die de gemeente kiest. Terugkerend: dat gebeurt bij hetzelfde laadpunt minstens {data?.instellingen?.patroonKeren ?? 1} keer per week, in minstens {data?.instellingen?.patroonMinimum ?? 3} van de laatste {data?.instellingen?.patroonWeken ?? 4} weken. Eenmalig: het gebeurde in die periode maar één keer. Bezetting buiten de controle-uren telt niet mee. Drukste moment: de weekdag en het uur waarop in deze wijk het vaakst een laadpunt te lang bezet was. Rotatietarief: of de gemeente het rotatietarief in deze wijk laat gelden (in te stellen bij Instellingen).</div>
            </div>

            <div style={st.kaart}>
              <h2 style={st.kaartKop}><span>Top 20 procent laadpunten <small style={st.klein}>de laadpunten waar een controle het meeste effect heeft</small></span>
                <span style={{ display: 'inline-flex', gap: 16, alignItems: 'center' }}>{eenmaligSchakel}
                <button type="button" style={st.knopLicht} disabled={!grensGekozen || !!bezig} onClick={() => download('excel')}>Excel</button></span></h2>
              <Tabel kop={[{ t: 'Adres' }, { t: 'Exploitant' }, { t: 'Wijk' }, { t: 'Laadpunten', g: 1 }, { t: 'Weken te lang bezet', g: 1 }, { t: 'Drukste moment' }, { t: '' }]}>
                {locatieRijen(topLocaties, 'top', 7)}
              </Tabel>
              <div style={st.voet}>Een aanwijzing, geen vaststelling. Of een auto nog laadde, blijkt ter plaatse of uit de sessiegegevens van de exploitant. Het adres is het dichtstbijzijnde adres uit het Adressenregister bij de plek van het laadpunt; de knop Kaart toont de precieze plek.</div>
            </div>
          </section>
        )}

        {/* ── TREND ── */}
        {tab === 'trend' && <Trend gemId={gemId} semi={ookSemi} grensGekozen={grensGekozen}
                                   versie={`${data?.meting?.laatste || ''}|${JSON.stringify(data?.instellingen || null)}`} />}

        {/* ── KAART ── */}
        <div style={{ display: tab === 'kaart' ? 'block' : 'none' }}>
          <Kaart zichtbaar={tab === 'kaart'} analyse={a} gekozen={gekozen} />
        </div>

        {/* ── LAADPUNTEN ── */}
        {tab === 'laadpunten' && (
          <div style={st.kaart}>
            <h2 style={st.kaartKop}><span>Alle laadpunten die te lang bezet zijn
              <small style={{ ...st.klein, marginLeft: 10 }}>{a ? `${getal(k.terugkerend)} terugkerend, ${getal(k.meerdere)} meerdere keren, ${getal(k.eenmalig)} eenmalig` : ''}</small></span>
              <span style={{ display: 'inline-flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                <label style={st.schakelKop}>
                  <input type="checkbox" checked={alleenStad} onChange={e => setAlleenStad(e.target.checked)} />
                  Alleen laadpunten met rotatietarief voor de stad
                </label>
                {eenmaligSchakel}</span></h2>
            <Tabel kop={[{ t: 'Adres' }, { t: 'Exploitant' }, { t: 'Wijk' }, { t: 'Laadpunten', g: 1 }, { t: 'Uren boven de grens', g: 1 }, { t: 'Weken te lang bezet', g: 1 }, { t: 'Soort' }, { t: 'Rotatietarief' }, { t: '' }]}>
              {locatieRijen(alleLocaties, 'alle', 9)}
            </Tabel>
            <div style={st.voet}>Te lang bezet: langer aaneen in gebruik dan de grens, binnen de controle-uren. Uren boven de grens: de tijd die een laadpunt langer bezet bleef dan de grens, opgeteld over de laatste {data?.instellingen?.patroonWeken ?? 4} weken. De duur is op een uur nauwkeurig, omdat er elk uur gemeten wordt. Het adres is het dichtstbijzijnde adres uit het Adressenregister; de knop Kaart toont de precieze plek van het laadpunt. Eén regel per locatie: laadpunten met hetzelfde adres en dezelfde exploitant. Een klik op de regel toont de EVSE-nummers. Rotatietarief: nieuwe concessie (tarief van de nieuwe concessie, opbrengst voor de stad), bestaande concessie (tarief van de lopende concessie, opbrengst voor de exploitant), geen (geen rotatietarief, of de wijk doet niet mee).</div>
          </div>
        )}

        {/* ── ROOSTER ── */}
        {tab === 'rooster' && (() => {
          // Per weekdag en uur: hoeveel laadpunten gemiddeld te lang bezet zijn.
          const pu = a?.rooster?.perUur;
          const keuze = roosterWijk && pu?.wijken?.[roosterWijk] ? pu.wijken[roosterWijk] : pu?.alle;
          const uren = pu?.uren || [];
          const max = Math.max(1, ...(keuze?.raster || []).flat().filter(v => v != null));
          const kleur = (n) => { const f = n / max; return n === 0 ? '#F7F9F9' : f > 0.75 ? '#9CC2CB' : f > 0.5 ? '#C9DDE1' : f > 0.25 ? '#DCE9EC' : '#EEF4F5'; };
          const dagen = ['maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag', 'zondag'];
          const cel = { ...st.td, textAlign: 'center', padding: '10px 4px', fontVariantNumeric: 'tabular-nums' };
          return (
            <div style={st.kaart}>
              <h2 style={st.kaartKop}>Wanneer laadpunten te lang bezet zijn <small style={st.klein}>gemiddeld aantal laadpunten dat op dat uur te lang bezet is</small></h2>
              <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', padding: '14px 18px 4px' }}>
                <label htmlFor="rwijk" style={{ ...st.label, marginBottom: 0 }}>Wijk</label>
                <select id="rwijk" value={roosterWijk} onChange={e => setRoosterWijk(e.target.value)} style={{ ...st.input, width: 'auto', minWidth: 220 }}>
                  <option value="">Alle wijken</option>
                  {Object.keys(pu?.wijken || {}).map(w => <option key={w} value={w}>{w}</option>)}
                </select>
              </div>
              {keuze?.beste && (
                <div style={{ padding: '10px 18px 6px', fontSize: 15 }}>
                  <b>Beste moment voor een controle:</b> {keuze.beste.weekdag} om {keuze.beste.uur} uur, gemiddeld {getal(keuze.beste.waarde)} {keuze.beste.waarde === 1 ? 'laadpunt' : 'laadpunten'} te lang bezet.
                </div>
              )}
              <div style={{ overflowX: 'auto' }}>
                <table style={st.table}>
                  <thead><tr><th style={st.th}>Dag</th>{uren.map(u => <th key={u} style={{ ...st.th, textAlign: 'center' }}>{u} u</th>)}</tr></thead>
                  <tbody>
                    {(keuze?.raster || []).map((rij, wd) => (
                      <tr key={wd}><td style={st.td}>{dagen[wd]}</td>
                        {rij.map((v, j) => v == null
                          ? <td key={j} style={{ ...cel, color: N.ink4 }}>–</td>
                          : <td key={j} style={{ ...cel, fontWeight: 600, background: kleur(v) }}>{getal(v)}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={st.voet}>Te lang bezet: langer dan de grens van {getal(data?.instellingen?.grensLangUren)} uur aaneen in gebruik. Gemiddelde van alle gemeten dagen met die weekdag. –: nog niet gemeten.</div>
            </div>
          );
        })()}

        {/* ── EXPLOITANTEN ── */}
        {tab === 'exploitanten' && (
          <div style={st.kaart}>
            <h2 style={st.kaartKop}>Per exploitant <small style={st.klein}>lijst met EVSE-nummers en tijdvakken, om door te sturen</small></h2>
            <Tabel kop={[{ t: 'Exploitant' }, { t: 'Gemeten laadpunten', g: 1 }, { t: 'Terugkerend te lang bezet', g: 1 }, { t: 'Niet beschikbaar', g: 1 }, { t: '' }]}>
              {(a?.perExploitant || []).map(e => (
                <tr key={e.exploitant}><td style={st.td}>{e.exploitant}</td><td style={tdG}>{getal(e.gemeten)}</td><td style={tdG}>{getal(e.terugkerend)}</td>
                  <td style={tdG}>{pct(e.nietBeschikbaar)}</td>
                  <td style={st.td}><button type="button" style={st.knopLicht} disabled={!grensGekozen || !!bezig}
                    onClick={() => download('exploitant', e.exploitant)}>Lijst (Excel)</button></td></tr>
              ))}
            </Tabel>
            <div style={st.voet}>De lijst bevat per keer te lang bezet het adres, de coördinaten met een kaartlink, het EVSE-nummer zoals de exploitant het schrijft, de datum, het eerste en laatste uur waarop het laadpunt bezet gemeten is, en het venster waarin de exploitant de sessie terugvindt (een uur ervoor tot een uur erna). De exploitant ziet welk token van welke MSP het was; de gemeente krijgt geen namen of tokens.</div>
          </div>
        )}

        {/* ── RAMING ── */}
        {tab === 'raming' && (() => {
          const r = a?.raming;
          const rot = r?.rotatie;
          const inv = r?.invoer || {};
          const kansPct = r ? Math.round(r.kansBezet * 1000) / 10 : null;
          return (
            <div style={st.kaart}>
              <h2 style={st.kaartKop}>Raming van de inkomsten per jaar <small style={st.klein}>bij twee, vier of zes controlerondes per week</small></h2>
              <div style={{ padding: '14px 18px 0' }}>
                <label style={{ ...st.schakel, marginTop: 0 }}>
                  <input type="checkbox" checked={velden.boeteNaarGemeente} onChange={e => zetVeld('boeteNaarGemeente')(e.target.checked)} />
                  Boetes voor parkeren op een laadplaats gaan naar de gemeente
                </label>
              </div>
              <div style={st.velden}>
                {velden.boeteNaarGemeente && <>
                {veld('boete', 'Bedrag boete (euro)', velden.boete, zetVeld('boete'), null, 'aan te leveren door de gemeente')}
                {veld('perronde', 'Laadpunten per controleronde', velden.laadpuntenPerRonde, zetVeld('laadpuntenPerRonde'),
                      'Hoeveel laadpunten per ronde gecontroleerd worden.')}
                {veld('kans', 'Kans op een boete (%)', velden.kansBoete, zetVeld('kansBoete'),
                      "Bij hoeveel procent van de te lang geparkeerde auto's de controleur een boete schrijft. Bijvoorbeeld 50 %.")}
                {veld('weken', 'Weken per jaar met controles', velden.wekenControle, zetVeld('wekenControle'),
                      'Bijvoorbeeld 48 als er vier weken per jaar niet gecontroleerd wordt.')}
                </>}
                {veld('nieuwelp', 'Aantal nieuwe laadpunten van de concessie', velden.nieuweLaadpunten, zetVeld('nieuweLaadpunten'),
                      'Voor de vooruitblik. Het aantal dat de nieuwe concessie in totaal plaatst.', 'bijvoorbeeld 631')}
              </div>
              <div style={{ padding: '0 18px 18px' }}><button type="button" style={st.knop} onClick={() => bewaar('raming')}>Opslaan</button></div>
              <div style={{ padding: '0 18px 14px', fontSize: 15 }}>
                <div><b>Rotatietarief nieuwe concessie, opbrengst voor de stad:</b>{' '}
                  {rot?.stadPerJaar != null ? `${getal(rot.stadPerJaar)} euro per jaar` : 'beschikbaar na vier weken meten'}</div>
                <div style={{ marginTop: 6 }}>
                  {rot?.nieuweLaadpunten == null
                    ? <><b>Vooruitblik:</b> vul hierboven het aantal nieuwe laadpunten van de concessie in.</>
                    : <><b>Vooruitblik bij {getal(rot.nieuweLaadpunten)} nieuwe laadpunten:</b>{' '}
                        {rot.vooruitblikPerJaar != null ? `${getal(rot.vooruitblikPerJaar)} euro per jaar` : 'beschikbaar na vier weken meten'}.
                        {' '}Gerekend met het gebruik van de bestaande publieke laadpunten.</>}
                </div>
                <div style={{ ...st.hulp, fontSize: 13.5, lineHeight: 1.55, marginTop: 12 }}>
                  Zo wordt het rotatietarief berekend:
                  <ol style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                    <li>Elk uur kijkt de module of een laadpunt in gebruik is.</li>
                    <li>Per laadsessie telt de module hoe lang het laadpunt aaneen in gebruik is, vanaf het eerste uur dat het in gebruik is gemeten.</li>
                    <li>Na {getal(rot?.startNaUren ?? 4)} uur rekent de module {getal(rot?.euroPerMinuut ?? 0.05)} euro per minuut, alleen voor de tijd tussen {rot?.van ?? 8} en {rot?.tot ?? 20} uur, met een maximum van {getal(rot?.maxEuro ?? 50)} euro per laadsessie.</li>
                    <li>Alleen laadpunten van de nieuwe concessie tellen mee, in de wijken waar het rotatietarief geldt.</li>
                    <li>Dat telt de module op over alle laadpunten en de hele meetperiode. Dat totaal gedeeld door het aantal gemeten dagen geeft de opbrengst van een gemiddelde dag; maal 365 is dat de opbrengst per jaar.</li>
                    <li>Vooruitblik: dezelfde berekening op de bestaande publieke laadpunten in de wijken waar het rotatietarief geldt, alsof het tarief daar al gold. De opbrengst per laadpunt maal het aantal nieuwe laadpunten geeft de vooruitblik.</li>
                  </ol>
                  Omdat er per uur gemeten wordt, is dit een schatting; de exploitant rekent per minuut.
                </div>
              </div>
              {velden.boeteNaarGemeente && <Tabel kop={[{ t: 'Controlerondes per week' }, { t: '2', g: 1 }, { t: '4', g: 1 }, { t: '6', g: 1 }]}>
                <tr><td style={st.td}>Boetes (euro per jaar)</td>
                  {[2, 4, 6].map(n => r?.boetes
                    ? <td key={n} style={tdG}>{getal(r.boetes[n])}</td>
                    : <td key={n} style={{ ...tdG, color: N.ink3 }}>vul de vier velden in</td>)}</tr>
              </Tabel>}
              <div style={st.voet}>
                {velden.boeteNaarGemeente && r?.boetes && <>Boetes per jaar = rondes per week × {getal(inv.wekenControle)} weken × {getal(inv.laadpuntenPerRonde)} laadpunten per ronde × {getal(kansPct)} % kans dat het laadpunt te lang bezet is × {getal(inv.kansBoete)} % kans op een boete × {getal(inv.boete)} euro. </>}
                Handhaving is bedoeld om laadplekken vrij te maken, niet om geld op te halen. Hoe beter het werkt, hoe minder het opbrengt.
                {' '}Op de laadpunten van de bestaande concessie gaat de opbrengst van het rotatietarief naar de exploitant, niet naar de stad.
              </div>
            </div>
          );
        })()}

        {/* ── GEGEVENS INLEZEN ── */}
        {tab === 'inlezen' && (
          <div style={st.kaart}>
            <h2 style={st.kaartKop}>Gegevens inlezen <small style={st.klein}>bestanden die de gemeente ontvangt van een exploitant, MOW of Fluvius</small></h2>
            <div style={st.velden}>
              <div>
                <label htmlFor="soort" style={st.label}>Soort bestand</label>
                <select id="soort" value={soort} onChange={e => setSoort(e.target.value)} style={st.input}>
                  <option value="sessies">Sessiegegevens van een exploitant (laadsessies met begin, einde en kWh)</option>
                  <option value="mow" disabled>Maandcijfers per laadpunt van MOW (kWh en bezettingsgraad), nog niet beschikbaar</option>
                  <option value="fluvius">Gebruiksdata per laadpaal van Fluvius (kWh en laadbeurten per kwartaal)</option>
                  <option value="concessie">Laadpunten van de nieuwe concessie (lijst met EVSE-nummers van de stad of de exploitant)</option>
                  <option value="deelauto">Laadpunten voor deelauto's (lijst met EVSE-nummers)</option>
                </select>
                {soort === 'sessies' && <div style={st.hulp}>Laadsessies in het OCPI-formaat (CDR's), als json-bestand.</div>}
                {soort === 'fluvius' && <div style={st.hulp}>Excel-bestand van Fluvius met een blad Gebruiksdata.</div>}
                {(soort === 'concessie' || soort === 'deelauto') && <div style={st.hulp}>Excel- of csv-bestand met een kolom EVSE-nummer.</div>}
              </div>
              <div>
                <label htmlFor="bestand" style={st.label}>Bestand</label>
                <div
                  onDragOver={e => e.preventDefault()}
                  onDrop={e => { e.preventDefault(); leesIn(e.dataTransfer.files?.[0]); }}
                  onClick={() => bestandRef.current?.click()}
                  style={{ border: `1.5px dashed ${N.line2}`, borderRadius: 8, padding: 18, textAlign: 'center', color: N.ink3,
                           fontSize: 14, background: N.paper2, cursor: 'pointer' }}>
                  {bezig === 'inlezen' ? 'Bezig met inlezen…' : 'Sleep een Excel- of csv-bestand hierheen, of kies een bestand'}
                </div>
                <input id="bestand" ref={bestandRef} type="file" accept=".xlsx,.xls,.csv,.json"
                       style={{ display: 'none' }} onChange={e => leesIn(e.target.files?.[0])} />
              </div>
            </div>
            <Tabel kop={[{ t: 'Bestand' }, { t: 'Soort' }, { t: 'Periode' }, { t: 'Regels', g: 1 }, { t: 'Gekoppeld', g: 1 }, { t: 'Ingelezen op' }]}>
              {bestanden.map(b => (
                <tr key={b.id}><td style={st.td}>{b.bestandsnaam || ''}</td><td style={st.td}>{soortNamen[b.soort] || b.soort}</td>
                  <td style={st.td}>{b.periode || ''}</td><td style={tdG}>{getal(b.regels)}</td><td style={tdG}>{getal(b.gekoppeld)}</td>
                  <td style={st.td}>{datumTekst(b.ingelezen_op)}</td></tr>
              ))}
            </Tabel>
            <div style={st.voet}>Een ingelezen bestand wordt via het EVSE-nummer aan de gemeten laadpunten gekoppeld. Gekoppeld: het aantal regels uit het bestand dat aan een gemeten laadpunt gekoppeld is. Het token van de bestuurder wordt nooit bewaard. Een lijst met laadpunten voor deelauto's haalt die laadpunten uit de hele analyse. Van elke soort lijst telt alleen de laatst ingelezen lijst.</div>
          </div>
        )}

        {/* ── INSTELLINGEN ── */}
        {tab === 'instellingen' && (
          <div style={st.kaart}>
            <h2 style={st.kaartKop}>Instellingen voor {data?.gemeente?.naam || ''}</h2>
            <div style={st.velden}>
              <div>
                <label htmlFor="cu" style={st.label}>Controle-uren</label>
                <input id="cu" type="text" value={velden.controle} onChange={e => zetVeld('controle')(e.target.value)} style={st.input} />
                <div style={{ ...st.hulp, lineHeight: 1.45, color: controleGeldig ? N.ink3 : N.red }}>{controleGeldig
                  ? 'Eén aaneengesloten blok van hele uren per dag, bijvoorbeeld 8-20 of 7-19. Alleen binnen deze uren telt een laadpunt als te lang bezet.'
                  : 'Vul één blok van hele uren in, bijvoorbeeld 8-20.'}</div>
              </div>
              <div>
                <label htmlFor="gr" style={st.label}>Grens voor te lang bezet (uren)</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input id="gr" type="text" value={velden.grens} placeholder="te kiezen na twee weken meten"
                         onChange={e => { setGrensGezet(''); zetVeld('grens')(e.target.value); }} style={{ ...st.input, width: 90 }} />
                  <button type="button" style={st.knopLicht} disabled={!data?.meting?.laatste} onClick={() => setToonGrensHulp(true)}>Hulp bij kiezen grens</button>
                </div>
                <div style={st.hulp}>Een laadpunt dat overdag langer in gebruik is dan deze grens, telt als te lang bezet.</div>
              </div>
            </div>
            <div style={{ borderTop: `1px solid ${N.line}`, padding: '16px 18px 4px' }}>
              <h3 style={{ margin: 0, fontSize: 15 }}>Terugkerend te lang bezet</h3>
              <p style={{ margin: '6px 0 12px', color: N.ink2, lineHeight: 1.55, fontSize: 14 }}>Een laadpunt is terugkerend te lang bezet als het niet één keer, maar week na week langer bezet is dan de grens. Dat zijn de laadpunten waar een controle het meeste oplevert. Met de drie velden hieronder bepaalt de gemeente hoe streng die regel is.</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, paddingBottom: 14 }}>
                {veld('pw', 'Periode in weken', velden.weken, zetVeld('weken'), 'Hoeveel weken terug de module kijkt.')}
                {veld('pm', 'Weken met te lang bezet', velden.minimum, zetVeld('minimum'), 'In hoeveel weken van die periode een laadpunt te lang bezet moet zijn.')}
                {veld('pk', 'Keren per week', velden.keren, zetVeld('keren'), 'Aantal keren dat een laadpunt per week te lang bezet moet zijn om mee te tellen.')}
              </div>
              {data?.meting?.laatste && (() => {
                const g = Number(String(velden.grens).replace(',', '.'));
                const c = hulp && Number.isFinite(g) && g >= 1 ? hulp.grenzen[Math.floor(g)] : null;
                return (
                  <div style={{ margin: '0 0 14px', padding: '9px 12px', borderRadius: 8, background: N.head, border: `1px solid ${N.line}`, fontSize: 14.5,
                                color: hulpFout ? N.red : N.ink }}>
                    {hulpFout ? hulpFout
                      : !controleGeldig ? 'Vul geldige controle-uren in om het effect te zien.'
                      : !hulp ? 'Bezig met rekenen…'
                      : c ? <>Met deze instellingen zijn <b>{aantalTekst(c.terugkerend)}</b> ({deelVan(c.terugkerend, hulp.laadpunten)}) terugkerend te lang bezet, bij de grens van {getal(g)} uur en controle-uren van {hulp.controle.van} tot {hulp.controle.tot} uur.</>
                      : `Vul een grens in van 1 tot en met ${hulp.maxGrens} uur om het effect te zien.`}
                  </div>);
              })()}
            </div>
            {grensGezet && <div style={{ margin: '0 18px 14px', padding: '9px 12px', borderRadius: 8, background: '#EAF2F4', color: N.accent, fontSize: 14 }}>{grensGezet}</div>}
            <h3 style={{ fontSize: 15, fontWeight: 700, padding: '16px 18px 0', margin: 0 }}>Rotatietarief</h3>
            <div style={st.velden}>
              {veld('rs', 'Rotatietarief gaat in na (uren)', velden.rotatieStart, zetVeld('rotatieStart'), 'Volgens de concessieleidraad AC Mechelen-Leuven.')}
              {veld('ru', 'Uren van het rotatietarief', velden.rotatieUren, zetVeld('rotatieUren'))}
              {veld('rt', 'Tarief (euro per minuut)', velden.rotatieTarief, zetVeld('rotatieTarief'), 'De stad kan het tarief tijdens de concessie aanpassen.')}
              {veld('rm', 'Maximum per laadsessie (euro)', velden.rotatieMax, zetVeld('rotatieMax'))}
              {veld('cs', 'Startdatum nieuwe concessie', velden.concessieStart, zetVeld('concessieStart'),
                    'Een laadpunt van de concessiehouder dat na deze datum voor het eerst gemeten wordt, valt onder de nieuwe concessie. Een ingelezen lijst gaat voor.',
                    'bijvoorbeeld 1-12-2026')}
              <div>
                <label htmlFor="ch" style={st.label}>Concessiehouder nieuwe concessie</label>
                <select id="ch" value={velden.concessieHouder} onChange={e => zetVeld('concessieHouder')(e.target.value)} style={st.input}>
                  {houderKeuzes.map(h => <option key={h} value={h}>{h}</option>)}
                </select>
                <div style={st.hulp}>De exploitant zoals die in de meting heet.</div>
              </div>
            </div>
            <div style={{ margin: '0 18px 18px', border: `1px solid ${N.line}`, borderRadius: 8, paddingBottom: 12 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, padding: '12px 18px 0', margin: 0 }}>Wijken waar het rotatietarief geldt</h3>
              <div style={{ display: 'flex', gap: 10, padding: '10px 18px 0' }}>
                <button type="button" style={st.knopLicht} onClick={() => setVelden(o => ({ ...o, wijkenUit: [] }))}>Alles aan</button>
                <button type="button" style={st.knopLicht}
                  onClick={() => setVelden(o => ({ ...o, wijkenUit: (data?.wijken || []).map(w => String(w.id)) }))}>Alles uit</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: '6px 18px', padding: '12px 18px 6px' }}>
                {(data?.wijken || []).map(w => (
                  <label key={w.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, color: N.ink, cursor: 'pointer' }}>
                    <input type="checkbox" checked={wijkAan(w.id)} onChange={e => zetWijk(w.id, e.target.checked)} /> {w.naam}
                  </label>
                ))}
              </div>
              <div style={{ ...st.hulp, padding: '4px 18px 0' }}>Standaard staan alle wijken aan. In een wijk die uit staat, telt geen enkel laadpunt mee voor de opbrengst van het rotatietarief.</div>
            </div>
            <div style={{ padding: '0 18px 18px' }}><button type="button" style={st.knop} onClick={() => bewaar('instellingen')}>Opslaan</button></div>
          </div>
        )}
      </main>

      {toonGrensHulp && <GrensHulp hulp={hulp} hulpFout={hulpFout || (!controleGeldig ? 'Vul geldige controle-uren in om de cijfers te zien.' : '')}
                                   grens={velden.grens} semi={ookSemi} rotatieStart={data?.instellingen?.rotatie?.startNaUren}
                                   regel={{ weken: velden.weken, minimum: velden.minimum, keren: velden.keren }}
                                   onKies={(g) => { zetVeld('grens')(String(g)); setGrensGezet(`Grens op ${g} uur gezet. Klik op Opslaan om te bewaren.`); setToonGrensHulp(false); }}
                                   onSluit={sluitGrensHulp} />}
      {toonUitleg && <Uitleg instellingen={data?.instellingen} gemeente={data?.gemeente?.naam} toegang={data?.toegang}
                             onSluit={() => setToonUitleg(false)} />}
    </div>
  );
}
